import { connect } from 'react-redux';
import { createSelector, createStructuredSelector } from 'reselect';
import FrameworkUtils from 'utils/framework';
import { orderDetailsSelector } from 'selectors/order/orderDetails/orderDetailsSelector';
import itemsByBasketSelector from 'selectors/order/orderDetails/items/itemsByBasket/itemsByBasketSelector';
import { orderSelector } from 'selectors/order/orderSelector';
import ShippingMethods from 'components/FrictionlessCheckout/ShippingMethods/ShippingMethods';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import orderUtils from 'utils/Order';
import Actions from 'Actions';
import Empty from 'constants/empty';
import OrderActions from 'actions/OrderActions';
import historyLocationActions from 'actions/framework/HistoryLocationActions';
import { refreshCheckout } from 'services/Checkout';
import { withSplitEDDProps } from 'viewModel/sharedComponents/splitEDD/withSplitEDDProps';
import withGlobalModals from 'hocs/withGlobalModals';
import { SECTION_NAMES } from 'constants/frictionlessCheckout';
import ShippingMethodsEN from 'components/FrictionlessCheckout/ShippingMethods/locales/ShippingMethods_en_US';
import ShippingMethodsFR from 'components/FrictionlessCheckout/ShippingMethods/locales/ShippingMethods_fr_CA';

const {
    SHIPPING_GROUPS: { SAME_DAY, GIFT }
} = orderUtils;
const { wrapHOC } = FrameworkUtils;
const { getTextFromResource, getLocalisedDataFromResource } = LanguageLocaleUtils;
const getText = getLocalisedDataFromResource(ShippingMethodsEN, ShippingMethodsFR);

const localization = createStructuredSelector({
    free: getTextFromResource(getText, 'free'),
    getItShipped: getTextFromResource(getText, 'getItShipped'),
    items: getTextFromResource(getText, 'items'),
    title: getTextFromResource(getText, 'title'),
    shipping: getTextFromResource(getText, 'shipping'),
    autoReplenish: getTextFromResource(getText, 'autoReplenish'),
    deliveryFrequency: getTextFromResource(getText, 'deliveryFrequency'),
    chooseThisShippingSpeed: getTextFromResource(getText, 'chooseThisShippingSpeed'),
    shippingMethodType: getTextFromResource(getText, 'shippingMethodType'),
    expeditedText: getTextFromResource(getText, 'expeditedText'),
    changeShippingMethod: getTextFromResource(getText, 'changeShippingMethod'),
    waived: getTextFromResource(getText, 'waived'),
    waiveShippingHandling: getTextFromResource(getText, 'waiveShippingHandling'),
    shippingRestrictions: getTextFromResource(getText, 'shippingRestrictions')
});

function calculateItemsQuantity(items, excludeGifCard = false) {
    if (excludeGifCard) {
        return items.filter(item => item.sku.type !== 'Gift Card').reduce((acc, item) => acc + item.qty, 0);
    }

    return items.reduce((acc, item) => acc + item.qty, 0);
}

const functions = {
    showContentModal: Actions.showContentModal,
    showAutoReplenishProductsModal: Actions.showAutoReplenishProductsModal,
    updateWaiveShipping: OrderActions.updateWaiveShipping,
    refreshCheckout,
    setActiveSection: OrderActions.setCheckoutActiveSection,
    setShippingMehthodNeedsConfirmation: OrderActions.setShippingMehthodNeedsConfirmation,
    goToURL: historyLocationActions.goTo
};

const fields = createSelector(
    orderSelector,
    orderDetailsSelector,
    localization,
    itemsByBasketSelector,
    (_ownState, ownProps) => ownProps.isSdd,
    (_ownState, ownProps) => ownProps.isPhysicalGiftCard,
    (order, orderDetails, locales, itemsByBasket = [], isSdd, isPhysicalGiftCard) => {
        const isSDDAndGiftCardOnly =
            orderDetails?.shippingGroups?.shippingGroupsEntries?.filter(
                group => group.shippingGroupType !== SAME_DAY && group.shippingGroupType !== GIFT
            )?.length === 0;
        let shippingGroup;
        let isElectronicShippingGroup = false;
        let electronicShippingGroup = null;
        let isGISAndElectronicShippingGroup = false;

        if (isSDDAndGiftCardOnly) {
            shippingGroup = orderUtils.getPhysicalGiftCardShippingGroup(orderDetails);
        } else {
            shippingGroup = orderUtils.getHardGoodShippingGroup(orderDetails);
            electronicShippingGroup = orderUtils.getElectronicsShippingGroup(orderDetails);
            isGISAndElectronicShippingGroup = !!(shippingGroup && electronicShippingGroup);
        }

        if (isSdd && !shippingGroup) {
            shippingGroup = orderUtils.getElectronicsShippingGroup(orderDetails);
            isElectronicShippingGroup = true;
        }

        const allItemsAreReplen = orderUtils.allItemsInBasketAreReplen(orderDetails);
        const orderHasReplen = orderUtils.hasAutoReplenishItems(orderDetails);

        const standardBasket = itemsByBasket?.find(item => item.basketType === 'ShipToHome') || {};

        const standardItems = standardBasket.items.filter(item => !item.isReplenishment);
        const autoReplenishItems = standardBasket.items.filter(item => item.isReplenishment);
        const orderShippingMethods = order.orderShippingMethods || Empty.Array;
        const shippingMethods = orderShippingMethods[shippingGroup?.shippingGroupId];
        const shippingMethodSelected = shippingGroup?.shippingMethod?.shippingFee
            ? shippingGroup?.shippingMethod
            : shippingMethods?.reduce((min, method) => {
                const minShippingFee = parseFloat(min.shippingFee?.substring(1));
                const methodShippingFee = parseFloat(method.shippingFee?.substring(1));

                return methodShippingFee < minShippingFee ? method : min, shippingMethods[0];
            }, []);

        const standardItemsQuantity = calculateItemsQuantity(standardItems, !!isPhysicalGiftCard && !isSdd);
        const sectionLevelError =
            order.sectionErrors?.[SECTION_NAMES.SHIPPING_METHOD]?.length && order.sectionErrors?.[SECTION_NAMES.SHIPPING_METHOD];

        const containsHazmatItems = !!orderDetails?.items?.items?.some(item => item.sku?.isHazmat);

        return {
            shippingGroup,
            allItemsAreReplen,
            orderHasReplen,
            standardBasket,
            locales,
            standardItemsQuantity,
            shippingMethods,
            shippingMethodSelected,
            orderId: orderDetails?.header?.orderId,
            orderDetails,
            sectionLevelError,
            ...(orderHasReplen && {
                replenishItemsQuantity: calculateItemsQuantity(autoReplenishItems),
                isReplenOnly: standardItemsQuantity === 0
            }),
            sectionName: SECTION_NAMES.SHIPPING_METHOD,
            isElectronicShippingGroup,
            isSDDAndGiftCardOnly,
            ...(isGISAndElectronicShippingGroup && { electronicShippingGroup }),
            containsHazmatItems
        };
    }
);

const withShippingMethodProps = wrapHOC(connect(fields, functions));

export default withGlobalModals(withShippingMethodProps(withSplitEDDProps(ShippingMethods)));
